import { AlertButton } from "@/Components/NextBase/AlertButton";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { api, queryClient } from "@/lib/api-client";
import { GetApiKeyById } from "@/server-types";
import {
  addToast,
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Snippet,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import { Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

type ManageApiKeyForm = {
  name: string;
  scopes: GetApiKeyById["response"]["apiKey"]["scopes"];
  locationIds: string[];
  userId: string | undefined;
};

const ManageApiKey = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const { data: apiKey, isPending: isGetApiKeyByIdLoading } =
    api.integrations.useGetApiKeyById(Number(id));

  const {
    mutateAsync: postApiKey,
    data: postApiKeyData,
    processedErrorMessage: postApiKeyErrorMessage,
    isPending: isPostApiKeyLoading,
  } = api.integrations.usePostApiKey();

  const {
    mutateAsync: putApiKeyById,
    processedErrorMessage: putApiKeyByIdErrorMessage,
    isPending: isPutApiKeyByIdLoading,
  } = api.integrations.usePutApiKeyById(Number(id));

  const { data: locations, isPending: areLocationsLoading } =
    api.location.useGetLocations({
      sortBy: "labelAsc",
    });

  const {
    mutateAsync: deleteApiKeyById,
    isPending: isDeleteApiKeyByIdLoading,
  } = api.integrations.useDeleteApiKeyById(Number(id));

  const { data: users, isPending: areUsersLoading } = api.user.useUserList();

  const form = useForm<ManageApiKeyForm>({
    initialValues: {
      name: "",
      scopes: [],
      locationIds: [],
      userId: undefined,
    },
    validate: {
      name: (value) => {
        if (value.length < 0) {
          return t`Vnesite ime ključa`;
        }
      },
      scopes: (value) => {
        if (value.length < 0) {
          return t`Vnesite vsaj en scope`;
        }
      },
      userId: (value) => {
        if (!value) {
          return t`Vnesite uporabnika`;
        }
      },
    },
  });
  const onClose = () => {
    navigate("/dashboard/integrations/api");
    queryClient.invalidateQueries({
      queryKey: ["getApiKeys"],
    });
  };

  useEffect(() => {
    if (apiKey) {
      form.initialize({
        name: apiKey.apiKey.name,
        scopes: apiKey.apiKey.scopes,
        locationIds: apiKey.apiKey.locationIds.map(String),
        userId: apiKey.apiKey.userId.toString(),
      });
    }
  }, [apiKey]);

  const scopesMap: {
    label: string;
    key: GetApiKeyById["response"]["apiKey"]["scopes"][number];
  }[] = [
    {
      label: "Luda",
      key: "luda",
    },
  ];

  const locationsSelectItems =
    locations?.locations.map((location) => ({
      label: location.label,
      key: location.locationId.toString(),
    })) || [];

  const usersSelectItems =
    users?.users.map((user) => ({
      label: user.name ?? "",
      key: user.userId.toString(),
    })) || [];

  const modalTitle =
    id === "-1" ? t`Dodajanje API ključa` : t`Urejanje API ključa`;

  const isAddingNew = id === "-1";

  const handleSubmit = async (values: ManageApiKeyForm) => {
    if (isAddingNew) {
      await postApiKey({
        name: values.name,
        scopes: values.scopes,
        locations: values.locationIds.map(Number),
        userId: Number(values.userId),
      });

      return;
    }

    await putApiKeyById({
      name: values.name,
      scopes: values.scopes,
      locations: values.locationIds.map(Number),
    });

    onClose();
  };

  const buttonSection = () => {
    if (postApiKeyData) {
      return <Button onPress={onClose}>{t`Zapri`}</Button>;
    }

    if (isAddingNew) {
      return (
        <div className="flex w-full flex-col gap-2">
          <Button
            color="primary"
            type="submit"
            isLoading={isPostApiKeyLoading}
          >{t`Dodaj`}</Button>
          <LimeAlert message={postApiKeyErrorMessage}></LimeAlert>
        </div>
      );
    }

    return (
      <div className="flex w-full flex-col gap-2">
        <div className="flex gap-2">
          <Button
            color="primary"
            type="submit"
            className="flex-1"
            isLoading={isPutApiKeyByIdLoading}
          >{t`Shrani`}</Button>
          <AlertButton
            buttonProps={{
              className: "flex-1",
              color: "danger",
            }}
            title={t`Izbriši API ključ?`}
            content={t`Ste prepričani da želite izbrisati API ključ?`}
            confirmProps={{
              label: t`Izbriši`,
              onClick: async () => {
                await deleteApiKeyById();
                onClose();
              },
              isLoading: isDeleteApiKeyByIdLoading,
            }}
          >{t`Izbriši`}</AlertButton>
        </div>
        <LimeAlert message={putApiKeyByIdErrorMessage}></LimeAlert>
      </div>
    );
  };

  return (
    <div>
      <LimeModal isOpen={true} onClose={onClose}>
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          className="flex flex-col gap-4"
        >
          <ModalContent>
            <ModalHeader>{modalTitle}</ModalHeader>
            <ModalBody>
              {postApiKeyData ? (
                <div className="flex w-full flex-col gap-2">
                  <span>
                    <Trans>API ključ bo prikazan samo enkrat!</Trans>
                  </span>

                  <div
                    className="flex cursor-pointer flex-col gap-2 rounded-md bg-gray-200 p-2"
                    onClick={() => {
                      navigator.clipboard.writeText(postApiKeyData?.fullApiKey);

                      addToast({
                        title: t`API ključ kopiran`,
                        description: t`API ključ je bil kopiran v odložišče`,
                      });
                    }}
                  >
                    <div className="break-words">
                      {postApiKeyData?.fullApiKey}
                    </div>

                    <div className="text-sm text-gray-500">
                      <Trans>Kopiraj API ključ s klikom na sivo polje</Trans>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <LimeInput label="Ime" {...form.getInputProps("name")} />
                  <LimeSelect
                    label="Uporabnik"
                    {...form.getInputProps("userId")}
                    items={usersSelectItems}
                    disabled={!isAddingNew}
                    isLoading={areUsersLoading}
                  />
                  <LimeSelect
                    label="Scope"
                    selectedKeys={form.values.scopes}
                    onSelectionChange={(value) => {
                      form.setFieldValue(
                        "scopes",
                        Array.from(
                          value,
                        ) as GetApiKeyById["response"]["apiKey"]["scopes"],
                      );
                    }}
                    items={scopesMap}
                    selectionMode="multiple"
                    disallowEmptySelection={false}
                  />
                  <LimeSelect
                    label="Lokacije"
                    items={locationsSelectItems}
                    selectionMode="multiple"
                    selectedKeys={form.values.locationIds}
                    onSelectionChange={(value) => {
                      form.setFieldValue(
                        "locationIds",
                        Array.from(value).map(String),
                      );
                    }}
                    disallowEmptySelection={false}
                    isLoading={areLocationsLoading}
                  />
                </>
              )}
            </ModalBody>
            <ModalFooter>{buttonSection()}</ModalFooter>
          </ModalContent>
        </form>
      </LimeModal>
    </div>
  );
};

export default ManageApiKey;
